import { useEffect, useState } from "react";

import {
  issueKey,
  listKeys,
  listProjectS3Credentials,
  listProjects,
  revokeS3Credential,
  revokeKey,
  rotateKey,
  type ApiKeyIssuedResponse,
  type ApiKeyMetaResponse,
  type ProjectRow,
  type S3CredentialRow,
} from "../api";

const SCOPES = ["read", "write", "delete", "share", "admin", "backup"] as const;

/**
 * 🔑 Keys — issue/rotate/revoke API keys from the dashboard. Thin UI over
 * the already-fully-routed `admin_api.routes.keys` (`KEY_MANAGE`). The raw
 * secret is shown exactly once, right after issue/rotate, in a dismissible
 * banner with an explicit "copy now" note — `listKeys` never returns it
 * (`ApiKeyMetaResponse` has no `api_key` field at all), so there is no way
 * for this page to ever render a secret it wasn't handed at that exact
 * moment.
 */
export function Keys(): JSX.Element {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [keys, setKeys] = useState<ApiKeyMetaResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [projectId, setProjectId] = useState("");
  const [scopes, setScopes] = useState<Set<string>>(new Set(["read"]));
  const [justIssued, setJustIssued] = useState<ApiKeyIssuedResponse | null>(null);
  const [s3Project, setS3Project] = useState("");
  const [s3Creds, setS3Creds] = useState<S3CredentialRow[]>([]);

  function loadS3(project: string): void {
    setS3Project(project);
    if (!project) {
      setS3Creds([]);
      return;
    }
    listProjectS3Credentials(project)
      .then(setS3Creds)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }

  async function handleRevokeS3(id: string): Promise<void> {
    if (!window.confirm("Revoke this S3 credential? Clients using it stop working at once.")) return;
    setBusyId(id);
    setError(null);
    try {
      await revokeS3Credential(id);
      loadS3(s3Project);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  function reload(): void {
    listProjects()
      .then((rows) => {
        setProjects(rows);
        if (!projectId && rows.length > 0) setProjectId(rows[0].id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    listKeys()
      .then(setKeys)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }

  useEffect(reload, []);

  function toggleScope(scope: string): void {
    setScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  }

  async function handleIssue(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!projectId || scopes.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const issued = await issueKey(projectId, Array.from(scopes));
      setJustIssued(issued);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleRotate(apiKeyId: string): Promise<void> {
    setBusyId(apiKeyId);
    setError(null);
    try {
      const issued = await rotateKey(apiKeyId);
      setJustIssued(issued);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function handleRevoke(apiKeyId: string): Promise<void> {
    if (!window.confirm("Revoke this key? It stops working immediately.")) return;
    setBusyId(apiKeyId);
    setError(null);
    try {
      await revokeKey(apiKeyId);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  function projectName(id: string): string {
    return projects.find((p) => p.id === id)?.name ?? id.slice(0, 8) + "…";
  }

  return (
    <div>
      <h2 className="page-title">🔑 Keys</h2>
      <p className="page-subtitle">
        Issue, rotate, revoke API keys. The secret is shown exactly once — copy it now.
      </p>
      {error && <div className="error-banner">{error}</div>}

      {justIssued && (
        <div className="panel" style={{ borderColor: "var(--accent)" }}>
          <h3>New key issued — copy it now, it will never be shown again</h3>
          <p className="page-subtitle">
            Project: {projectName(justIssued.project_id)} · Scopes: {justIssued.scopes.join(", ")}
          </p>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              fontSize: 13,
              userSelect: "all",
            }}
          >
            {justIssued.api_key}
          </pre>
          <button onClick={() => setJustIssued(null)}>I've copied it — dismiss</button>
        </div>
      )}

      <div className="panel">
        <h3>Issue API key</h3>
        <form onSubmit={(e) => void handleIssue(e)}>
          <div className="form-row">
            <label htmlFor="key-project">Project</label>
            <select
              id="key-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              required
            >
              <option value="" disabled>
                Select a project…
              </option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>Scopes</label>
            {SCOPES.map((scope) => (
              <label key={scope} style={{ minWidth: "auto", marginRight: 10 }}>
                <input
                  type="checkbox"
                  checked={scopes.has(scope)}
                  onChange={() => toggleScope(scope)}
                  style={{ width: "auto", marginRight: 4 }}
                />
                {scope}
              </label>
            ))}
          </div>
          <button type="submit" disabled={busy || !projectId || scopes.size === 0}>
            {busy ? "Issuing…" : "Issue key"}
          </button>
        </form>
      </div>

      <div className="panel">
        <h3>Existing keys</h3>
        <table>
          <thead>
            <tr>
              <th>Project</th>
              <th>Fingerprint</th>
              <th>Scopes</th>
              <th>Status</th>
              <th>Expires</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id}>
                <td>{projectName(k.project_id)}</td>
                <td>
                  <code>{k.fingerprint}</code>
                </td>
                <td>{k.scopes.join(", ")}</td>
                <td>
                  <span className="badge">{k.status}</span>
                </td>
                <td>{k.expires_at ? new Date(k.expires_at).toLocaleString() : "never"}</td>
                <td>
                  <button
                    disabled={busyId === k.id || k.status !== "active"}
                    onClick={() => void handleRotate(k.id)}
                  >
                    Rotate
                  </button>{" "}
                  <button
                    className="danger"
                    disabled={busyId === k.id || k.status !== "active"}
                    onClick={() => void handleRevoke(k.id)}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h3>S3 credentials</h3>
        <p className="page-subtitle">
          Minted by tenants with their own API key (<code>POST /api/v1/s3-credentials</code>) for
          the S3-compatible gateway. Revoking the API key they were minted with also disables them.
        </p>
        <div className="form-row">
          <label htmlFor="s3-project">Project</label>
          <select id="s3-project" value={s3Project} onChange={(e) => loadS3(e.target.value)}>
            <option value="">Select a project…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        {s3Project && (
          <table>
            <thead>
              <tr>
                <th>Access key</th>
                <th>Name</th>
                <th>Bucket</th>
                <th>Scopes</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {s3Creds.map((c) => (
                <tr key={c.id}>
                  <td>
                    <code>{c.access_key_id}</code>
                  </td>
                  <td>{c.name || "—"}</td>
                  <td>{c.bucket}</td>
                  <td>{c.scopes.join(", ")}</td>
                  <td>
                    <span className="badge">
                      {c.status}
                      {c.status === "active" && !c.usable ? " (parent key revoked)" : ""}
                    </span>
                  </td>
                  <td>{new Date(c.expires_at).toLocaleDateString()}</td>
                  <td>{c.last_used_at ? new Date(c.last_used_at).toLocaleString() : "never"}</td>
                  <td>
                    <button
                      className="danger"
                      disabled={busyId === c.id || c.status !== "active"}
                      onClick={() => void handleRevokeS3(c.id)}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
              {s3Creds.length === 0 && (
                <tr>
                  <td colSpan={8} className="page-subtitle">
                    No S3 credentials in this project.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
